<template>
  <div class="animated fadeIn" v-permission="PERMISSIONS.VIEW_PORTFOLIO">
    <b-card :no-body="true" footer-class="px-3 py-2 card-footer-action">
      <b-card-body class="p-3 clearfix">
        <b-row>
          <b-col cols="8">
            <i class="fa fa-shield bg-primary p-3 font-2xl mr-3 float-left"></i>
            <div class="h5 mb-0 mt-2">{{ vulnLabel }}</div>
            <div class="text-muted font-weight-bold font-xs">
              {{ sourceLabel }}
            </div>
            <div class="text-muted font-weight-bold font-xs">
              {{ vulnerability.title }}
            </div>
          </b-col>
          <b-col cols="4" class="align-middle" style="align-self: center;">
            <b-row class="float-right align-middle">
              <div style="height:40px;">
                <div class="text-center pull-left severity-bug-icon" v-bind:style="{ background: severityColor }">
                  <i class="fa fa-bug" style="font-size:20px; padding:10px" aria-hidden="true"></i>
                </div>
                <div class="text-center pull-left severity-bug-label">
                  <div style="font-size:20px; padding:6px">{{ severityLabel }}</div>
                </div>
              </div>
            </b-row>
          </b-col>
        </b-row>
      </b-card-body>
      <div id="vulnerability-info-footer" slot="footer">
        <b-link class="font-weight-bold font-xs btn-block text-muted" v-b-modal.vulnerabilityDetailsModal>{{ $t('message.view_details') }} <i class="fa fa-angle-right float-right font-lg"></i></b-link>
      </div>
    </b-card>
    <b-tabs class="body-bg-color" style="border-left: 0; border-right:0; border-top:0 ">
      <b-tab class="body-bg-color overview-chart" style="border-left: 0; border-right:0; border-top:0 " active>
        <template v-slot:title><i class="fa fa-line-chart"></i> {{ $t('message.overview') }}</template>

        <div v-if="vulnerability.description">
          <b-card :title="this.$t('message.overview')">
            <b-card-text>
              <vue-showdown :markdown="vulnerability.description" flavor="github"/>
            </b-card-text>
            <div v-if="cvssBaseScore > 0" slot="footer">
              <b-row class="text-center">
                <b-col class="mb-sm-2 mb-0">
                  <div class="text-muted">{{ $t('message.cvss_base_score') }}</div>
                  <strong>{{cvssBaseScore}}</strong>
                  <b-progress height={} class="progress-xs mt-2" :precision="1" :variant="vulnerability.severity" v-bind:value="(cvssBaseScore / 10) * 100"></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">{{ $t('message.cvss_impact_subscore') }}</div>
                  <strong>{{cvssImpactScore}}</strong>
                  <b-progress height={} class="progress-xs mt-2" :precision="1" variant="severity-info" v-bind:value="(cvssImpactScore / 10) * 100"></b-progress>
                </b-col>
                <b-col class="mb-sm-2 mb-0 d-sm-down-none">
                  <div class="text-muted">{{ $t('message.cvss_exploitability_subscore') }}</div>
                  <strong>{{cvssExploitScore}}</strong>
                  <b-progress height={} class="progress-xs mt-2" :precision="1" variant="severity-info" v-bind:value="(cvssExploitScore / 10) * 100"></b-progress>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </div>

        <div v-if="vulnerability.cwe">
          <b-card :title="this.$t('message.weakness')">
            <b-card-text>
              <a :href="cweLink">{{ cweLabel }}</a>
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.recommendation">
          <b-card :title="this.$t('message.recommendation')">
            <b-card-text>
              <vue-showdown :markdown="vulnerability.recommendation" />
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.references">
          <b-card :title="this.$t('message.references')">
            <b-card-text>
              <vue-showdown :markdown="vulnerability.references" />
            </b-card-text>
          </b-card>
        </div>

        <div v-if="vulnerability.credits">
          <b-card :title="this.$t('message.credits')">
            <b-card-text>
              <vue-showdown :markdown="vulnerability.credits" />
            </b-card-text>
          </b-card>
        </div>

      </b-tab>
      <b-tab v-if="isPermitted(PERMISSIONS.VULNERABILITY_ANALYSIS)">
        <template v-slot:title><i class="fa fa-tasks"></i> {{ $t('message.affected_projects') }} <b-badge variant="tab-total">{{ totalAffectedProjects }}</b-badge></template>
        <affected-projects :key="this.uuid" :source="source" :vulnId="vulnId" v-on:total="totalAffectedProjects = $event" />
      </b-tab>
    </b-tabs>
  <vulnerability-details-modal :vulnerability="cloneDeep(vulnerability)" v-on:projectUpdated="syncProjectFields"/>
  </div>
</template>

<script>
  import common from "../../../shared/common"
  import { getStyle } from '@coreui/coreui/dist/js/coreui-utilities'
  import PortfolioWidgetRow from "../../dashboard/PortfolioWidgetRow";
  import SeverityBarChart from "../../dashboard/SeverityBarChart";
  import EventBus from '../../../shared/eventbus';
  import permissionsMixin from "../../../mixins/permissionsMixin";
  import AffectedProjects from './AffectedProjects';
  import VulnerabilityDetailsModal from "./VulnerabilityDetailsModal";
  import {cloneDeep} from "lodash-es";

  export default {
    mixins: [permissionsMixin],
    components: {
      SeverityBarChart,
      PortfolioWidgetRow,
      AffectedProjects,
      VulnerabilityDetailsModal
    },
    title: '',
    computed: {
      vulnLabel () {
        return `${this.vulnId} (${this.source})`;
      },
      cvssBaseScore () {
        return common.valueWithDefault(((this.vulnerability.cvssV3BaseScore) ? this.vulnerability.cvssV3BaseScore : this.vulnerability.cvssV2BaseScore), 0);
      },
      cvssImpactScore () {
        return common.valueWithDefault(((this.vulnerability.cvssV3ImpactSubScore) ? this.vulnerability.cvssV3ImpactSubScore : this.vulnerability.cvssV2ImpactSubScore), 0);
      },
      cvssExploitScore () {
        return common.valueWithDefault(((this.vulnerability.cvssV3ExploitabilitySubScore) ? this.vulnerability.cvssV3ExploitabilitySubScore : this.vulnerability.cvssV2ExploitabilitySubScore), 0);
      },
      cweLabel() {
        if (this.vulnerability.cwe) {
          return `CWE-${this.vulnerability.cwe.cweId}: ${this.vulnerability.cwe.name}`;
        } else {
          return null;
        }
      },
      cweLink () {
        if (this.vulnerability.cwe) {
          return `https://cwe.mitre.org/data/definitions/${this.vulnerability.cwe.cweId}`;
        } else {
          return null;
        }
      },
      sourceLabel () {
        switch (this.vulnerability.source) {
          case 'NVD':
            return "National Vulnerability Database";
          case 'NPM':
            return "NPM Advisories";
          case 'OSSINDEX':
            return "Sonatype OSS Index";
          case 'VULNDB':
            return "VulnDB (Risk Based Security)";
          default:
            return "";
        }
      },
      severityLabel () {
        switch (this.vulnerability.severity) {
          case 'CRITICAL':
            return this.$t('severity.critical_severity');
          case 'HIGH':
            return this.$t('severity.high_severity');
          case 'MEDIUM':
            return this.$t('severity.medium_severity');
          case 'LOW':
            return this.$t('severity.low_severity');
          case 'INFO':
            return this.$t('severity.info_severity');
          default:
            return this.$t('severity.unassigned_severity');
        }
      },
      severityColor () {
        switch (this.vulnerability.severity) {
          case 'CRITICAL':
            return this.severityCritical;
          case 'HIGH':
            return this.severityHigh;
          case 'MEDIUM':
            return this.severityMedium;
          case 'LOW':
            return this.severityLow;
          case 'INFO':
            return this.severityInfo;
          default:
            return this.severityUnassigned;
        }
      }
    },
    data() {
      return {
        severityCritical: this.getStyle('--severity-critical'),
        severityHigh: this.getStyle('--severity-high'),
        severityMedium: this.getStyle('--severity-medium'),
        severityLow: this.getStyle('--severity-low'),
        severityUnassigned: this.getStyle('--severity-unassigned'),
        severityInfo: this.getStyle('--severity-info'),
        trackColor: this.getStyle('--component-active-color'),
        primaryColor: this.getStyle('--primary'),
        uuid: null,
        source: null,
        vulnId: null,
        vulnerability: {},
        totalAffectedProjects: 0
      }
    },
    methods: {
      cloneDeep: function(vulnerability) {
        return cloneDeep(vulnerability);
      },
      getStyle: function(style) {
        return getStyle(style);
      },
      syncProjectFields: function(vulnerability) {
        //todo future - when internal vulnerability editing is implemented
      }
    },
    beforeMount() {
      this.uuid = this.$route.params.uuid;
      this.source = this.$route.params.source;
      this.vulnId = this.$route.params.vulnId;
    },
    mounted() {
      let url = "";
      if (this.uuid) {
        url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/${this.uuid}`;
      } else {
        url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/source/${this.source}/vuln/${this.vulnId}`;
      }
      this.axios.get(url).then((response) => {
        this.vulnerability = response.data;
        if (!this.source) {
          this.source = response.data.source;
        }
        if (!this.vulnId) {
          this.vulnId = response.data.vulnId;
        }
        EventBus.$emit('addCrumb', this.vulnLabel);
        this.$title = `${this.$t('message.vulnerability')} ${this.vulnLabel}`;
      });
    },
    destroyed() {
      EventBus.$emit('crumble');
    }
  };
</script>
